import { Button, FormGroup, InputGroup } from "@blueprintjs/core"
import { CheckSquareOffset, Clock, Minus, Package } from "@phosphor-icons/react"
import { ArrowCounterClockwise } from "@phosphor-icons/react/dist/ssr"
import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import Divider from "components/Atoms/Divider"
import DynamicSelector from "components/Molecules/DynamicSelector"
import { PRODUCTION_LINE, USERS } from "graphql/selector.queries.gql"
import React, { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import { card } from "./InventoryCard"
import { ReverseModal } from "./ReverseModal"
import StackCardSummary from "./StackCardSummary"
import { InwardAllocationType } from "generated/graphql"
import { useLazyQuery } from "@apollo/client"
import { INWARD_ALLOCATION } from "graphql/production/allocation.queries.gql"
import CustomSpinner from "components/Atoms/CustomSpinner"
import { cloneDeep } from "lodash"
import ButtonMutation from "components/Molecules/ButtonMutation"
import AppToaster from "utils/AppToaster"
import {
  ADD_PACKING_ALLOTMENT,
  CREATE_INWARD_ALLOCATION,
} from "graphql/production/allocation.mutation.gql"
import Note from "components/Atoms/Note"
import ConfirmButtonMutation from "components/Templates/ConfirmButtonMutation"
import CustomTag from "components/Atoms/CustomTag"
import dayjs from "dayjs"

const AllocationDetail = () => {
  const { id } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [lines, setLines] = useState<any[]>([])
  const [inwardAllocation, setInwardAllocation] =
    useState<InwardAllocationType>(null)
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({})
  const [selectedLine, setSelectedLine] = useState<any>(null)
  // ---- Line Management ----
  function addLine() {
    const uuid = uuidv4()
    setLines(prev => {
      const copy = cloneDeep(prev)
      copy.unshift({
        _id: uuid,
        productionLine: null,
        supervisor: null,
        allotedQuantity: inwardAllocation?.availableSkusCount,
        allotedWeight: inwardAllocation?.availableWeight,
        sendToLine: false,
      })
      return copy
    })
  }

  function updateLine(id, path, value) {
    setLines(prev => {
      const copy = cloneDeep(prev)
      return copy.map(line =>
        line?.id === id || line?._id === id ? { ...line, [path]: value } : line
      )
    })
  }
  function deleteLine(id) {
    setLines(prev => prev.filter(line => line?.id !== id && line?._id !== id))
  }

  const [getInwardAllocation, { loading, refetch }] = useLazyQuery(
    INWARD_ALLOCATION,
    {
      onCompleted: data => {
        if (data?.inwardAllocation) {
          setInwardAllocation(data?.inwardAllocation)

          const allotments = cloneDeep(
            data?.inwardAllocation?.packingallotmentSet ?? []
          )

          allotments.sort((a, b) => {
            const aId = a?.id || a?._id
            const bId = b?.id || b?._id
            return bId.localeCompare(aId)
          })

          setLines(allotments)
        }
      },
      onError: err => console.error("GraphQL Error:", err),
    }
  )

  useEffect(() => {
    if (id && parseInt(id)) getInwardAllocation({ variables: { id } })
  }, [id])

  if (loading)
    return (
      <div className="p-4 flex justify-center items-center h-40">
        <CustomSpinner size={50} />
      </div>
    )

  const inward = inwardAllocation?.inward
  const weigh = inward?.weigh
  const confirm = JSON.parse(inwardAllocation?.metadata ?? "{}")?.confirm
  const isGreaterThanZero: boolean =
    (inwardAllocation?.availableSkusCount ?? 0) > 0

  const averagePerSku =
    weigh?.netWeight && weigh?.inwardedQuantity
      ? (Number(weigh.netWeight) / Number(weigh.inwardedQuantity)).toFixed(2)
      : "-"
  const formatWeight = (value?: number | string | null) => {
    const num = Number(value)
    return !isNaN(num) ? num.toFixed(2) : "-"
  }
  const hasUnsavedLine = lines?.some(line => !line?.id)

  return (
    <CustomCard className={"m-4"}>
      <div className=" w-full flex items-center">
        <span>
          <CustomButton
            icon="arrow-left"
            onClick={() => navigate("/d/allocation")}
            minimal
          />
        </span>
        <h5 className="ml-3">Holding Area</h5>
      </div>
      <Divider className="w-full mt-0" />

      {/* Inventory Status */}
      <div className="container mt-3 ">
        <h6 className="text-blue-600 font-500">Current Inventory Status</h6>
        <div className="grid grid-cols-3 gap-3">
          {card({
            Icon: Package,
            subtext: (
              <span>
                {formatWeight(weigh?.netWeight)}{" "}
                {weigh?.skuType?.capacityUnit?.codename ?? "Units"}{" "}
                <CustomTag round intent="primary">
                  {averagePerSku}{" "}
                  {weigh?.skuType?.capacityUnit?.codename ?? "Units"} / SKU
                </CustomTag>
              </span>
            ),
            info: "Inwarded Per SKU = Inwarded Quantity / Inwarded SKU",
            text: `${weigh?.inwardedQuantity?.toString() ?? "-"} ${
              weigh?.skuType?.name ?? "Units"
            }`,
            title: "Inward SKUs",
          })}

          {card({
            Icon: CheckSquareOffset,
            subtext: `${formatWeight(inwardAllocation?.allottedWeight)} ${
              weigh?.skuType?.capacityUnit?.codename ?? "Units"
            }`,
            text: `${inwardAllocation?.allottedSku?.toString() ?? "-"} ${
              weigh?.skuType?.name ?? "Units"
            }`,
            title: "Allotted SKUs",
            intent: "success",
          })}

          {card({
            Icon: Clock,
            subtext: `${formatWeight(inwardAllocation?.availableWeight)} ${
              weigh?.skuType?.capacityUnit?.codename ?? "Units"
            }`,
            text: `${inwardAllocation?.availableSkusCount?.toString() ?? "-"} ${
              weigh?.skuType?.name ?? "Units"
            }`,
            title: "Available SKUs",
            intent: "warning",
          })}
        </div>

        {/* Summary */}
        <section className="my-8">
          <StackCardSummary inwardAllocations={inwardAllocation} />
        </section>

        {/* Packing Allotment */}
        <section>
          <header className="flex justify-between items-center">
            <h6 className="text-blue-600 font-500">Allotment for Packing</h6>
            <CustomButton
              text="Line No."
              icon="add"
              intent="primary"
              onClick={addLine}
              disabled={!isGreaterThanZero || hasUnsavedLine}
            />
          </header>
          <Divider className="mt-0" />

          <div className="flex flex-col justify-between gap-4">
            {lines?.map(line => {
              const lineId = line?.id || line?._id
              return (
                <div
                  key={lineId}
                  className="border-solid border-[#D3DFE8] border-1 rounded-2xl p-4"
                >
                  <div className="text-right mb-2 flex items-center justify-between gap-2">
                    <div>
                      {" "}
                      {line?.id != null && (
                        <p className="text-gray-600">
                          {" "}
                          Assigned At:{" "}
                          {dayjs(line?.createdAt).format(
                            "Do MMMM YYYY, h:mm A"
                          )}{" "}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-4">
                      {lineErrors[lineId] && (
                        <div className="text-red-500 text-sm ">
                          {lineErrors[lineId]}
                        </div>
                      )}
                      {!line?.sendToLine ? (
                        <ButtonMutation
                          intent="success"
                          mutation={ADD_PACKING_ALLOTMENT}
                          variables={{
                            id: line?.id,
                            allotment: {
                              inwardAllocationId: inwardAllocation?.id,
                              productionLineId: line?.productionLine?.id,
                              supervisorId: line?.supervisor?.id,
                              allotedQuantity: Number(line?.allotedQuantity),
                              allotedWeight: Number(line?.allotedWeight),
                              sendToLine: true,
                            },
                          }}
                          validate={() => {
                            if (
                              !line?.productionLine?.id ||
                              !line?.supervisor?.id
                            ) {
                              setLineErrors(prev => ({
                                ...prev,
                                [lineId]:
                                  "Please select Line Number and Supervisor",
                              }))
                              return false
                            }
                            if (
                              !line?.allotedQuantity ||
                              line?.allotedQuantity <= 0
                            ) {
                              setLineErrors(prev => ({
                                ...prev,
                                [lineId]: "Please enter a valid SKU quantity",
                              }))
                              return false
                            }

                            if (
                              line?.allotedQuantity >
                              inwardAllocation?.availableSkusCount
                            ) {
                              setLineErrors(prev => ({
                                ...prev,
                                [lineId]:
                                  "Allotted quantity cannot exceed available SKU count",
                              }))
                              return false
                            }

                            setLineErrors(prev => {
                              const { [lineId]: _, ...rest } = prev
                              return rest
                            })
                            return true
                          }}
                          onCompleted={() => {
                            AppToaster.show({
                              message: "Line allotment successful",
                              icon: "tick",
                              intent: "success",
                            })
                            updateLine(lineId, "sendToLine", true)
                            refetch()
                          }}
                          onError={() => {
                            AppToaster.show({
                              message: "Unable to allot line",
                              icon: "cross",
                              intent: "danger",
                            })
                          }}
                          text={"Send to Line"}
                        />
                      ) : (
                        <Button
                          text="Reverse"
                          disabled={confirm === true ? true : false}
                          className="rounded-[3px] shadow-[#F5F8FA]"
                          onClick={() => {
                            params.set("lineId", lineId)
                            setParams(params)
                            setSelectedLine(line)
                          }}
                          icon={<ArrowCounterClockwise />}
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="grid grid-cols-4 gap-3 w-full">
                      <DynamicSelector
                        label="Allot Line no."
                        required
                        query={PRODUCTION_LINE}
                        onChange={(_, e) =>
                          updateLine(lineId, "productionLine", e)
                        }
                        variables={{
                          depotId:
                            inward?.booking?.schedule?.destinationDepot?.id,
                        }}
                        value={line?.productionLine}
                        disabled={line?.sendToLine}
                      />

                      <DynamicSelector
                        label="Assigned Supervisor"
                        required
                        query={USERS}
                        onChange={(_, e) => updateLine(lineId, "supervisor", e)}
                        value={line?.supervisor}
                        variables={{ group: "Line Supervisor" }}
                        searchVector={["fullName"]}
                        name={["fullName"]}
                        disabled={line?.sendToLine}
                      />

                      <FormGroup
                        className="mb-5"
                        label={
                          <>
                            Allot no. of SKUs{" "}
                            <span className="text-red">*</span>
                          </>
                        }
                        disabled={line?.sendToLine}
                        helperText={
                          !line?.id ? (
                            <span className="text-[#1F6D99] font-400">{`Available: ${inwardAllocation?.availableSkusCount} ${weigh?.skuType?.name}`}</span>
                          ) : (
                            ""
                          )
                        }
                      >
                        <InputGroup
                          type="number"
                          required
                          disabled={line?.sendToLine}
                          value={line?.allotedQuantity ?? ""}
                          onChange={e => {
                            const qty = Number(e.target.value)
                            updateLine(lineId, "allotedQuantity", qty)
                            updateLine(
                              lineId,
                              "allotedWeight",
                              (qty * (Number(averagePerSku) || 0))?.toFixed(2)
                            )
                          }}
                        />
                      </FormGroup>

                      <FormGroup
                        className="mb-5"
                        label={
                          <>
                            Allot Weight (kg){" "}
                            <span className="text-red">*</span>
                          </>
                        }
                        disabled={line?.sendToLine}
                        helperText={
                          !line?.id ? (
                            <span className="text-[#1F6D99]">{`Available : ${inwardAllocation?.availableWeight} kg`}</span>
                          ) : (
                            ""
                          )
                        }
                      >
                        <InputGroup
                          type="number"
                          disabled
                          value={line?.allotedWeight?.toString() ?? "0"}
                        />
                      </FormGroup>
                    </div>

                    <Button
                      className="!border-white !border-0 !bg-white mb-4 !shadow-none"
                      disabled={line?.sendToLine}
                      icon={
                        <Minus
                          className={line?.sendToLine ? "" : "text-red"}
                          onClick={() => deleteLine(lineId)}
                          weight="bold"
                        />
                      }
                    />
                  </div>
                  {line?.reverseQuantity > 0 && (
                    <Note intent="danger">
                      Reversed Allocation - {line?.productionLine?.name}:{" "}
                      <span className="text-gray-800">
                        {" "}
                        <strong>ReversedAt:</strong>{" "}
                        {dayjs(line?.updatedAt).format("Do MMM YYYY, h:mm A")}{" "}
                        <strong>Reversed:</strong> {line?.reverseQuantity} SKU's
                        | {line?.reverseWeight} KG |{" "}
                        <strong>Reversal Reason: </strong>{" "}
                        {line?.reversalReason}
                      </span>
                    </Note>
                  )}
                </div>
              )
            })}
          </div>
          {!isGreaterThanZero && (
            <div className="flex justify-end mt-2">
              <ConfirmButtonMutation
                mutation={CREATE_INWARD_ALLOCATION}
                disabled={confirm === true ? true : false}
                alert={
                  <span>
                    Are you sure you want to <strong> Confirm.</strong>
                  </span>
                }
                variables={{
                  id: inwardAllocation?.id,
                  input: {
                    inwardId: inwardAllocation?.inward?.id,
                    metadata: {
                      confirm: true,
                    },
                  },
                }}
                onError={() => {
                  AppToaster.show({
                    message: "Unable To Confirm",
                    icon: "cross",
                    intent: "danger",
                  })
                }}
                onCompleted={() => {
                  navigate("/d/allocation")
                }}
                text={"Confirm"}
              />
            </div>
          )}
        </section>
        <ReverseModal
          inwardAllocationId={inwardAllocation?.id}
          line={selectedLine}
          skuCapacity={Number(averagePerSku)}
          refetch={refetch}
        />
      </div>
    </CustomCard>
  )
}

export default AllocationDetail
